import React, { useState, useContext } from "react";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import { Box, Button, Container } from "@amzn/awsui-components-react";
import ErrorAlert from "../common/ErrorAlert";
import { RedriveHistory } from "./components/RedriveHistory";
import { useSelectedExecution } from "./hooks/useSelectedExecution";
import { useFileValidation } from "./hooks/useFileValidation";
import { StageSelector } from "./components/StageSelector";
import { FileUploader } from "./components/FileUploader";
import { useExecuteRedrive } from "./hooks/useExecuteRedrive";
import { ORDER_REDRIVE } from "./redriveKinds";
import { StageContext } from "../../contexes/OrderIdContext";
import { Stage } from "../../lusca-search-api/generated-src";

interface Props {
  setStage: React.Dispatch<React.SetStateAction<Stage>>;
}

export default function OrderRedriveContent({ setStage }: Props) {
  const stage = useContext(StageContext);
  const [loading, setLoading] = useState(false);
  const {
    selectedExecutionId,
    setSelectedExecutionId,
    selectExecution,
    clearSelection,
  } = useSelectedExecution();
  const {
    file,
    error,
    redriveOK,
    handleFileChange,
    validateRedrive,
    setError,
    removeDuplicates,
  } = useFileValidation();
  const { executeRedrive: executeRedriveHook } = useExecuteRedrive();

  const executeRedrive = async () => {
    if (file) {
      setLoading(true);
      await executeRedriveHook(file, stage, setSelectedExecutionId, setError);
      setLoading(false);
    }
  };

  return (
    <SpaceBetween size="xl">
      <div>
        <SpaceBetween size="l">
          {error && <ErrorAlert content={error} header="Redrive Error" />}
          {selectedExecutionId && (
            <Button
              iconName="arrow-left"
              onClick={clearSelection}
              variant="link"
            >
              Back to all redrives
            </Button>
          )}

          <Container header={<Box variant="h2">Order Redrive Tool</Box>}>
            <SpaceBetween size="m">
              <Box variant="p">
                This tool allows you to redrive orders that have already been
                processed. If an order is marked as healthy, but an error was
                present in an upstream system like FLARE, you will be able to
                reprocess the CBE Generated by lusca with this tool. You can
                upload a CSV file with Order IDs here, or redrive a single order
                on that order&#39;s page.
              </Box>
              {!selectedExecutionId && (
                <StageSelector stage={stage} setStage={setStage} />
              )}
            </SpaceBetween>
          </Container>

          {!selectedExecutionId && (
            <Container>
              <SpaceBetween size="m">
                <FileUploader
                  file={file}
                  handleFileChange={handleFileChange}
                  validateRedrive={validateRedrive}
                  redriveOK={redriveOK}
                  removeDuplicates={removeDuplicates}
                  loading={loading}
                />
                {file && redriveOK && (
                  <Button
                    onClick={executeRedrive}
                    variant="primary"
                    disabled={loading}
                    loading={loading}
                  >
                    Execute Redrive
                  </Button>
                )}
              </SpaceBetween>
            </Container>
          )}

          <RedriveHistory
            kind={ORDER_REDRIVE}
            stage={stage}
            selectedExecutionId={selectedExecutionId}
            onSelectExecution={selectExecution}
          />
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );
}
